import { getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getToken } from 'firebase/app-check';
import { appCheckInstance } from '../firebase';

function callAuthAdmin(payload) {
  const app = getApp();
  const region = import.meta.env.VITE_LOCATION || 'europe-west9';
  const functions = getFunctions(app, region);
  const fn = httpsCallable(functions, 'authAdmin');
  return fn(payload).then((result) => result.data);
}

/**
 * List authentication users.
 *
 * Supports server-side pagination and filtering.  The following options
 * are accepted:
 *   page?: number        // zero-based page index
 *   pageSize?: number    // items per page
 *   filterField?: string // firstName, lastName, email, name
 *   filterOperator?: string // startsWith, equals
 *   filterValue?: string // normalized on server
 * @param {{page?:number,pageSize?:number,name?:string,email?:string,filterField?:string,filterOperator?:string,filterValue?:string,limit?:number,pageToken?:string}} [opts]
 * @returns {Promise<any>}
 */
export function listUsers(opts = {}) {
  return callAuthAdmin({ action: 'list', ...opts });
}

/**
 * Get a single user by uid.
 * @param {string} uid
 * @returns {Promise<any>}
 */
export function getUser(uid) {
  if (!uid) return Promise.reject(new Error('getUser: missing uid'));
  return callAuthAdmin({ action: 'get', uid });
}

/**
 * Create a new authentication user.
 * @param {{email:string,password:string,displayName?:string,firstName?:string,lastName?:string,hospitalId?:string,disabled?:boolean,isAdmin?:boolean,isDemo?:boolean,isOffline?:boolean,mfaEnabled?:boolean}} data
 * @returns {Promise<any>}
 */
export function createUser(data) {
  if (!data || !data.email || !data.password) {
    return Promise.reject(new Error('createUser: email and password required')); 
  }
  return callAuthAdmin({ action: 'create', ...data });
}

/**
 * Update an existing user.
 * @param {{uid:string,email?:string,password?:string,displayName?:string,firstName?:string,lastName?:string,hospitalId?:string,disabled?:boolean,isAdmin?:boolean,isDemo?:boolean,isOffline?:boolean,mfaEnabled?:boolean}} data
 * @returns {Promise<any>}
 */
export function updateUser(data) {
  if (!data || !data.uid) {
    return Promise.reject(new Error('updateUser: uid required')); 
  }
  return callAuthAdmin({ action: 'update', ...data });
}

/**
 * Delete an auth user by uid.
 * @param {string} uid
 * @returns {Promise<any>}
 */
export function deleteUser(uid) {
  if (!uid) return Promise.reject(new Error('deleteUser: missing uid'));
  return callAuthAdmin({ action: 'delete', uid });
}

/**
 * Unenroll all MFA factors for a user.
 * @param {string} uid
 * @returns {Promise<any>}
 */
export function resetUserMfa(uid) {
  if (!uid) return Promise.reject(new Error('resetUserMfa: missing uid'));
  return callAuthAdmin({ action: 'resetMfa', uid });
}

// ─── Trusted Device ──────────────────────────────────────────────────────────

const TRUSTED_DEVICE_ENDPOINT = '/api/trustedDevice';

/**
 * Raw fetch is used instead of httpsCallable because the trusted device flow
 * relies on HttpOnly cookies, which the Firebase Functions SDK strips. A direct
 * fetch with `credentials: 'include'` preserves them end-to-end.
 * @param {object} payload
 * @returns {Promise<any>}
 */
async function callTrustedDeviceAdmin(payload) {
  const auth = getAuth(getApp());

  const [appCheckToken, idToken] = await Promise.all([
    appCheckInstance
      ? getToken(appCheckInstance).then(({ token }) => token).catch(() => {
          console.warn('App Check token unavailable');
          return '';
        })
      : Promise.resolve(''),
    auth.currentUser ? auth.currentUser.getIdToken() : Promise.resolve(null),
  ]);

  const headers = { 'Content-Type': 'application/json' };
  if (appCheckToken) headers['X-Firebase-AppCheck'] = appCheckToken;
  if (idToken) headers['Authorization'] = `Bearer ${idToken}`;

  const response = await fetch(TRUSTED_DEVICE_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ data: payload }),
  });

  const body = await response.json();

  if (!response.ok || body.error) {
    const err = new Error(body.error?.message ?? 'Unknown error');
    err.code = body.error?.message ?? 'unknown';
    throw err;
  }

  return body.result;
}

export function signInWithTrustedDevice(email, password) {
  if (!email || !password) return Promise.reject(new Error('signInWithTrustedDevice: email and password required'));
  return callTrustedDeviceAdmin({ action: 'signInWithTrustedDevice', email, password });
}

/**
 * Register this device as trusted and receive a cookie. Evicts the least-recently-used
 * device if the user is already at the device limit.
 * @param {string} deviceLabel
 * @returns {Promise<{ deviceId: string, evictedDeviceLabel?: string, evictedCount?: number, maxDevices?: number }>}
 */
export function trustDevice(deviceLabel) {
  return callTrustedDeviceAdmin({ action: 'trustDevice', deviceLabel });
}

/**
 * Clear the trusted device cookie for the current user.
 * @returns {Promise<{ success: boolean }>}
 */
export function clearTrustedDevice() {
  return callTrustedDeviceAdmin({ action: 'clearDeviceCookie' });
}

export function listTrustedDevices(uid) {
  if (!uid) return Promise.reject(new Error('listTrustedDevices: missing uid'));
  return callTrustedDeviceAdmin({ action: 'listTrustedDevices', uid });
}

export function revokeDevice(uid, deviceId) {
  if (!uid || !deviceId) return Promise.reject(new Error('revokeDevice: uid and deviceId required'));
  return callTrustedDeviceAdmin({ action: 'revokeDevice', uid, deviceId });
}

export function revokeAllDevices(uid) {
  if (!uid) return Promise.reject(new Error('revokeAllDevices: missing uid'));
  return callTrustedDeviceAdmin({ action: 'revokeAllDevices', uid });
}
